<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisOperationalPage from 'dashboard/components-next/LaisOperationalPage.vue';
import LaisMetricStrip from 'dashboard/components-next/LaisMetricStrip.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisCardSkeleton from 'dashboard/components-next/LaisCardSkeleton.vue';
import LaisEntitySummaryCard from 'dashboard/components-next/LaisEntitySummaryCard.vue';
import SurveyResponsesTable from './components/SurveyResponsesTable.vue';
import SurveyResponseDrawer from './components/SurveyResponseDrawer.vue';
import SurveyScoreDistribution from './components/SurveyScoreDistribution.vue';
import { FIELD_SELECT_CLASS, SURVEY_ROUTE_NAMES } from './constants';
import {
  canExportSurveyResponses,
  formatScore,
} from './helper/surveyResponseHelper';
import { useSurveyResponses } from './composables/useSurveyResponses';
import { useSurveyLabels } from './composables/useSurveyLabels';

/**
 * Answers collected by a single survey form.
 *
 * The metric strip, the distribution card and the table always describe the
 * same slice: period and status filters are applied to both requests.
 */
const route = useRoute();
const router = useRouter();
const { t, kindLabel, kindIcon, responseStatusOptions, periodOptions } =
  useSurveyLabels();

const formId = route.params.formId;
const currentUser = useMapGetter('getCurrentUser');

const {
  form,
  questions,
  responses,
  metrics,
  total,
  totalPages,
  page,
  filters,
  isLoadingResponses,
  isLoadingMetrics,
  isDownloading,
  isEmpty,
  hasError,
  errorMessage,
  load,
  fetchResponse,
  downloadCsv,
  refresh,
  setFilter,
  goToPage,
} = useSurveyResponses(formId);

const selectedResponse = ref(null);

const canExport = computed(() =>
  canExportSurveyResponses(currentUser.value, route.params.accountId)
);

const pageError = computed(() =>
  hasError.value ? errorMessage.value || t('SURVEYS.RESPONSES.ERROR') : false
);

const statusFilter = computed({
  get: () => filters.value.status,
  set: value => setFilter('status', value),
});

const periodFilter = computed({
  get: () => filters.value.periodDays,
  set: value => setFilter('periodDays', Number.parseInt(value, 10) || 0),
});

const metricItems = computed(() => [
  {
    id: 'sent',
    label: t('SURVEYS.RESPONSES.METRICS.SENT'),
    value: metrics.value.totalSent,
    icon: 'i-lucide-send',
  },
  {
    id: 'submitted',
    label: t('SURVEYS.RESPONSES.METRICS.SUBMITTED'),
    value: metrics.value.totalSubmitted,
    tone: 'positive',
    icon: 'i-lucide-circle-check',
  },
  {
    id: 'rate',
    label: t('SURVEYS.RESPONSES.METRICS.RATE'),
    value: `${metrics.value.responseRate}%`,
    tone: 'info',
    icon: 'i-lucide-percent',
  },
  {
    id: 'score',
    label: t('SURVEYS.RESPONSES.METRICS.AVERAGE_SCORE'),
    value: formatScore(metrics.value.averageScore),
    icon: 'i-lucide-star',
  },
]);

const summaryBadges = computed(() => {
  if (!form.value) return [];
  return [
    { label: kindLabel(form.value.kind), tone: 'info' },
    {
      label: form.value.active
        ? t('SURVEYS.LIST.STATUS.ACTIVE')
        : t('SURVEYS.LIST.STATUS.INACTIVE'),
      tone: form.value.active ? 'positive' : 'neutral',
    },
  ];
});

const summaryMeta = computed(() => {
  if (!form.value) return [];
  return [
    {
      label: t('SURVEYS.RESPONSES.SUMMARY.QUESTIONS'),
      value: form.value.questions.length,
    },
    {
      label: t('SURVEYS.RESPONSES.SUMMARY.TOTAL'),
      value: total.value,
    },
  ];
});

const pageLabel = computed(() =>
  t('SURVEYS.RESPONSES.PAGINATION.LABEL', {
    page: page.value,
    total: totalPages.value,
  })
);

const goToList = () => {
  router.push({ name: SURVEY_ROUTE_NAMES.index });
};

const goToEditor = () => {
  router.push({
    name: SURVEY_ROUTE_NAMES.edit,
    params: { accountId: route.params.accountId, formId },
  });
};

const openResponse = async response => {
  selectedResponse.value = response;
  const responseId = response?.id;
  if (!responseId) return;

  const detail = await fetchResponse(responseId);
  if (detail && selectedResponse.value?.id === responseId) {
    selectedResponse.value = detail;
  }
};

const closeResponse = () => {
  selectedResponse.value = null;
};

const filenameFromDisposition = value => {
  const match = /filename="?([^";]+)"?/i.exec(value || '');
  return match?.[1] || `form-responses-${formId}.csv`;
};

const saveCsv = ({ blob, contentDisposition }) => {
  const url = URL.createObjectURL(
    blob instanceof Blob
      ? blob
      : new Blob([blob], { type: 'text/csv;charset=utf-8' })
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filenameFromDisposition(contentDisposition);
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const exportCsv = async () => {
  const result = await downloadCsv();
  if (!result.ok) {
    if (!result.duplicate) {
      useAlert(result.message || t('SURVEYS.RESPONSES.EXPORT_ERROR'));
    }
    return;
  }

  saveCsv(result);
  useAlert(t('SURVEYS.RESPONSES.EXPORT_SUCCESS'));
};

onMounted(load);
</script>

<template>
  <LaisOperationalPage
    :title="form ? form.name : t('SURVEYS.RESPONSES.TITLE')"
    :helper="t('SURVEYS.RESPONSES.HELPER')"
    :error="pageError"
    show-back
    :aside-label="t('SURVEYS.RESPONSES.SUMMARY.ARIA_LABEL')"
    @back="goToList"
    @retry="load"
  >
    <template #actions>
      <LaisPrimaryButton
        v-if="canExport"
        variant="ghost"
        icon="i-lucide-download"
        :is-loading="isDownloading"
        @click="exportCsv"
      >
        {{ t('SURVEYS.RESPONSES.EXPORT_CSV') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton
        variant="ghost"
        icon="i-lucide-refresh-cw"
        :is-loading="isLoadingResponses"
        @click="refresh"
      >
        {{ t('SURVEYS.RESPONSES.REFRESH') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton icon="i-lucide-pencil" @click="goToEditor">
        {{ t('SURVEYS.RESPONSES.EDIT_FORM') }}
      </LaisPrimaryButton>
    </template>

    <template #metrics>
      <LaisMetricStrip
        :items="metricItems"
        :loading="isLoadingMetrics"
        :aria-label="t('SURVEYS.RESPONSES.METRICS.ARIA_LABEL')"
      />
    </template>

    <template #filters>
      <div class="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end">
        <label class="flex min-w-0 flex-col gap-1.5 sm:w-56">
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('SURVEYS.RESPONSES.FILTERS.PERIOD') }}
          </span>
          <select v-model="periodFilter" :class="FIELD_SELECT_CLASS">
            <option
              v-for="option in periodOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="flex min-w-0 flex-col gap-1.5 sm:w-56">
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('SURVEYS.RESPONSES.FILTERS.STATUS') }}
          </span>
          <select v-model="statusFilter" :class="FIELD_SELECT_CLASS">
            <option
              v-for="option in responseStatusOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
      </div>
    </template>

    <div class="flex min-w-0 flex-col gap-4">
      <SurveyScoreDistribution
        :distribution="metrics.scoreDistribution"
        :loading="isLoadingMetrics"
      />

      <LaisCardSkeleton v-if="isLoadingResponses" :count="3" mode="row" />

      <div
        v-else-if="isEmpty"
        class="grid place-items-center gap-3 rounded-lais border border-dashed border-lais-border bg-lais-bg-card px-4 py-16 text-center"
      >
        <span
          class="i-lucide-inbox size-10 text-lais-text-muted"
          aria-hidden="true"
        />
        <p class="text-lais-body-md font-semibold text-lais-text">
          {{ t('SURVEYS.RESPONSES.EMPTY_TITLE') }}
        </p>
        <p class="max-w-md text-lais-body-sm text-lais-text-helper">
          {{ t('SURVEYS.RESPONSES.EMPTY_BODY') }}
        </p>
      </div>

      <template v-else>
        <SurveyResponsesTable
          :responses="responses"
          :questions="questions"
          @select="openResponse"
        />

        <div
          v-if="totalPages > 1"
          class="flex flex-wrap items-center justify-between gap-3"
        >
          <span class="text-lais-body-sm text-lais-text-muted">
            {{ pageLabel }}
          </span>
          <div class="flex items-center gap-2">
            <LaisPrimaryButton
              variant="ghost"
              size="sm"
              icon="i-lucide-chevron-left"
              :disabled="page <= 1"
              @click="goToPage(page - 1)"
            >
              {{ t('SURVEYS.RESPONSES.PAGINATION.PREVIOUS') }}
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              size="sm"
              icon="i-lucide-chevron-right"
              :disabled="page >= totalPages"
              @click="goToPage(page + 1)"
            >
              {{ t('SURVEYS.RESPONSES.PAGINATION.NEXT') }}
            </LaisPrimaryButton>
          </div>
        </div>
      </template>
    </div>

    <template #aside>
      <LaisEntitySummaryCard
        v-if="form"
        :title="form.name"
        :subtitle="form.description"
        :icon="kindIcon(form.kind)"
        :badges="summaryBadges"
        :meta="summaryMeta"
        :aria-label="t('SURVEYS.RESPONSES.SUMMARY.ARIA_LABEL')"
      />
    </template>
  </LaisOperationalPage>

  <SurveyResponseDrawer
    :show="Boolean(selectedResponse)"
    :response="selectedResponse"
    :questions="questions"
    @close="closeResponse"
  />
</template>
